import React, { useRef, useState } from 'react';
import { HStack, Stack } from '@chakra-ui/react';
import { boolean, date, object, SchemaOf, string } from 'yup';
import { FormWidgetProps } from '@melio/ap-widgets';
import { Text } from '@melio/penny';
import { Form, useMelioForm } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';

import { usePlatformIntl } from '@/translations/Intl';
import { ExportFileFormWidgetFields, exportFormat } from './CompanySettings.types';

type ExportFileFormatProps = FormWidgetProps<ExportFileFormWidgetFields> & { includeMultipleFormats: boolean };

const schema = object().shape({
  fileFormatRadioValue: string().default(exportFormat.CSV).oneOf(Object.values(exportFormat)).required(),
  fromDate: date().required(),
  toDate: date().required(),
  includePayments: boolean().required(),
}) as SchemaOf<ExportFileFormWidgetFields>;

const useReportLabels = () => {
  const { formatMessage } = usePlatformIntl();
  const chooseDateTitle = formatMessage('widgets.createReportModal.form.chooseDateTitle');
  const fromLabel = formatMessage('widgets.createReportModal.form.startDate.label');
  const toLabel = formatMessage('widgets.createReportModal.form.endDate.label');
  const formatsLabel = formatMessage('widgets.createReportModal.form.formatsLabel');
  const csvLabel = formatMessage('widgets.createReportModal.form.formats.csv');
  const iifLabel = formatMessage('widgets.createReportModal.form.formats.iif');
  const qifLabel = formatMessage('widgets.createReportModal.form.formats.qif');
  const includePaymentsLabel = formatMessage('widgets.createReportModal.form.includePaymentsLabel');
  return [chooseDateTitle, fromLabel, toLabel, formatsLabel, csvLabel, iifLabel, qifLabel, includePaymentsLabel];
};

export const CreateReportFormatWidget = forwardRef<ExportFileFormatProps, 'form'>(
  ({ defaultValues, onSubmit, onSubmissionStateChange, includeMultipleFormats, ...props }, ref) => {
    const [minToDate, setMinToDate] = useState<Date>(defaultValues?.fromDate ?? new Date());
    const [chooseDateTitle, fromLabel, toLabel, formatsLabel, csvLabel, iifLabel, qifLabel, includePaymentsLabel] =
      useReportLabels();

    const { formProps, registerField, setValue, watch } = useMelioForm<ExportFileFormWidgetFields>({
      onSubmit,
      schema,
      defaultValues,
      onSubmissionStateChange,
    });

    const toDateRef = useRef<HTMLInputElement>(null);

    const onFromChange = (fromDate: Date) => {
      const toDate = new Date(watch('toDate'));
      const newMinToDate = new Date(fromDate.getTime() + 24 * 60 * 60 * 1000); //newMinToDate = fromDate + 24h
      setMinToDate(newMinToDate);
      if (newMinToDate.getTime() >= toDate.getTime()) {
        setValue('toDate', newMinToDate);
      }
      setValue('fromDate', fromDate);
      toDateRef?.current?.click();
    };

    return (
      <Form data-component="CreateFormatForm" {...props} {...formProps} ref={ref}>
        <Form.Checkbox
          {...registerField('includePayments')}
          label={includePaymentsLabel}
          aria-label={includePaymentsLabel}
        />
        <Text>{chooseDateTitle}</Text>
        <HStack>
          <Stack flexDirection="column" flexGrow={1}>
            <Form.DateField
              labelProps={{ label: fromLabel }}
              {...registerField('fromDate')}
              excludeHolidays={false}
              onChange={(date) => {
                if (date) {
                  onFromChange(date);
                }
              }}
              weekDays={[0, 1, 2, 3, 4, 5, 6]}
            />
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Form.DateField
              labelProps={{ label: toLabel }}
              {...registerField('toDate')}
              minDate={minToDate}
              ref={toDateRef}
              excludeHolidays={false}
              weekDays={[0, 1, 2, 3, 4, 5, 6]}
            />
          </Stack>
        </HStack>
        {includeMultipleFormats && (
          <Form.RadioGroup
            labelProps={{ label: formatsLabel }}
            {...registerField('fileFormatRadioValue')}
            isDisabled={false}
            options={[
              {
                mainLabelProps: { label: csvLabel },
                value: exportFormat.CSV,
              },
              {
                mainLabelProps: { label: iifLabel },
                value: exportFormat.IIF,
              },
              {
                mainLabelProps: { label: qifLabel },
                value: exportFormat.QIF,
              },
            ]}
          />
        )}
      </Form>
    );
  },
);

CreateReportFormatWidget.displayName = 'CreateFormatForm';
